import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'Heating Services',
    description: 'When Las Vegas nights get cold, you need a reliable heating system. We install, repair, and maintain furnaces, heat pumps, and ductless mini-splits. Whether you need emergency furnace repair or a complete system replacement, we\'ve got you covered.',
    features: ['Furnace installation & repair', 'Heat pump services', 'Ductless heating', 'Thermostat upgrades'],
    image: '/images/service-image2.png',
    alt: 'Heating services - Seas Heating and AC',
  },
  {
    title: 'Cooling Services',
    description: 'In the Vegas heat, your AC isn\'t optional—it\'s essential. We specialize in central air conditioning installation, repair, and maintenance. Regular tune-ups keep your system efficient and extend its lifespan, saving you money and avoiding mid-summer breakdowns.',
    features: ['AC installation & replacement', 'Air conditioning repair', 'Refrigerant recharge', 'Evaporator coil services'],
    image: '/images/service-image3.png',
    alt: 'Cooling services - Seas Heating and AC',
  },
  {
    title: 'Maintenance & Tune-Ups',
    description: 'Preventive maintenance is the best investment you can make in your HVAC system. Our maintenance plans include thorough inspections, filter changes, and performance optimization. Stay comfortable year-round and avoid costly emergency repairs.',
    features: ['Seasonal tune-ups', 'Filter replacement', 'System efficiency checks', 'Duct inspection'],
    image: '/images/service-image4.png',
    alt: 'Maintenance and tune-ups - Seas Heating and AC',
  },
];

export default function Services() {
  useEffect(() => {
    document.title = 'Our Services | Seas Heating and AC';
  }, []);
  return (
    <>
      {/* Page Header */}
      <section className="bg-seas-blue text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-white/80 text-sm font-semibold uppercase tracking-widest mb-2">What We Do</p>
          <h1 className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight">Our Services</h1>
          <p className="text-gray-300 max-w-2xl">
            Heating, cooling, and maintenance for Las Vegas homes and businesses. 
            Quality work backed by 19 years of experience.
          </p>
        </div>
      </section>

      {/* Intro + image section */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <p className="text-seas-blue/80 text-sm font-semibold uppercase tracking-widest mb-2">Overview</p>
              <h2 className="text-2xl font-bold text-seas-gray mb-4 tracking-tight">Full-Service HVAC for Home & Business</h2>
              <p className="text-gray-600 leading-relaxed">
                Seas Heating and AC offers complete heating, cooling, and maintenance solutions. From new installations 
                and system replacements to repairs and tune-ups, we handle it all. Our goal is simple: keep your space 
                comfortable, your equipment running efficiently, and your mind at ease.
              </p>
            </div>
            <div className="h-[260px] sm:h-[280px] w-full overflow-hidden rounded-xl">
              <img src="/images/service-image1.png" alt="Full-service HVAC - Seas Heating and AC" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-16 lg:py-24 bg-gray-50/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <h2 className="text-2xl lg:text-3xl font-bold text-seas-gray mb-4">{service.title}</h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-gray-700">
                        <span className="text-seas-gold">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`h-[260px] sm:h-[280px] w-full overflow-hidden rounded-xl ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img src={service.image} alt={service.alt} className="w-full h-full object-cover" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/contact"
              className="inline-block bg-seas-gold text-white font-semibold px-8 py-3 rounded-lg hover:bg-amber-600 transition-colors"
            >
              Get a Free Quote
            </Link>
          </div>

          {/* Additional image section */}
          <section className="mt-24 pt-16 border-t border-gray-200">
            <p className="text-seas-blue/80 text-sm font-semibold uppercase tracking-widest text-center mb-2">Gallery</p>
            <h2 className="text-2xl font-bold text-seas-gray text-center mb-8 tracking-tight">Our Work in Action</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { src: '/images/ourwork-image1.png', alt: 'HVAC work - Seas Heating and AC' },
                { src: '/images/ourwork-image2.png', alt: 'HVAC installation - Seas Heating and AC' },
                { src: '/images/ourwork-image3.png', alt: 'HVAC service - Seas Heating and AC' },
                { src: '/images/ourwork-image4.png', alt: 'HVAC repair - Seas Heating and AC' },
              ].map(({ src, alt }) => (
                <div key={src} className="h-[200px] sm:h-[220px] w-full overflow-hidden rounded-xl bg-gray-100">
                  <img src={src} alt={alt} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        </div>
      </section>
    </>
  );
}
